import React, { useEffect, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { publicPostApi } from '../../api';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';



const RegisterPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Logged-in users have no business on /register — mirror the landing page.
  useEffect(() => {
    if (user) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);

  const SignupSchema = Yup.object().shape({
    user_type: Yup.string()
      .oneOf(['seller', 'buyer'], 'Please select a valid user type')
      .required('User type is required!'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required!'),
    password: Yup.string()
      .required('Password is required!')
      .max(128, 'Password cannot exceed 128 characters'),
    given_name: Yup.string()
      .max(255, 'Given name cannot exceed 255 characters'),
    family_name: Yup.string()
      .max(255, 'Family name cannot exceed 255 characters'),
    phone_number: Yup.string()
      .required('Phone number is required!')
      .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    phone_number2: Yup.string()
      .matches(/^\d{10}$|^$/, 'Phone number 2 must be exactly 10 digits or empty')
      .nullable(),
    pan_number: Yup.string()
      .required('PAN number is required!')
      .matches(/^\d{9}$/, 'PAN number must be exactly 9 digits'),
    business_registration: Yup.mixed()
      .required('Business registration document is required'),
    verification_document: Yup.mixed()
      .required('Verification document is required'),
  });

  const handleSubmit = async (values, formikHelpers) => {
    setIsLoading(true);
    try {
      // Convert file to Base64 with proper format
      const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            // Keep the full data URL format
            resolve(reader.result);
          };
          reader.onerror = error => reject(error);
        });
      };

      // Prepare the payload
      const payload = { ...values, is_staff: false };
      
      // Convert files to Base64 if they exist
      if (values.business_registration) {
        payload.business_registration = await getBase64(values.business_registration);
      }
      
      if (values.verification_document) {
        payload.verification_document = await getBase64(values.verification_document);
      }

      try {
        await publicPostApi('system/users/register/', payload);
        toast.success('Sign up successful');
        navigate('/login');
      } catch (requestError) {
        const responseData = requestError?.data || {};
        if (responseData.error === "You are not yet verified. Please contact support staff to track progress of your verification.") {
          navigate('/pending-approval');
        } else {
          // Parse backend validation errors
          const errors = {};
          const errorMessages = responseData.error?.split('/') || [];
          errorMessages.forEach((msg) => {
            const [fieldPart, ...message] = msg.split(':');
            const field = fieldPart.trim().toLowerCase();
            const errorMessage = message.join(':').trim();
            errors[field] = errorMessage;
          });

          formikHelpers.setErrors(errors);
          toast.error(responseData.error || 'Registration failed. Please check the form for errors.');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.message !== "You are not yet verified...") {
        toast.error(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-black">Create Account</h2>
            <p className="text-sm md:text-base text-gray-500 mt-2 mb-8 sm:mb-10">
              Register with your information
            </p>
          </div>

          <Formik
            validationSchema={SignupSchema}
            initialValues={{
              user_type: '',
              given_name: '',
              family_name: '',
              email: '',
              password: '',
              phone_number: '',
              phone_number2: '',
              pan_number: '',
              is_staff: false,
              business_registration: null,
              verification_document: null,
            }}
            onSubmit={(values, formikHelpers) => {
              handleSubmit(values, formikHelpers);
            }}
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form className="flex flex-col justify-center">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* User Type Field */}
                  <div className="grid sm:col-span-2">
                    <label className="block text-gray-500 font-medium text-sm leading-none mb-3">
                      User Type *
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Buyer Card */}
                      <label className={`relative cursor-pointer ${values.user_type === 'buyer' ? 'ring-2 ring-brand-500' : ''}`}>
                        <Field
                          type="radio"
                          name="user_type"
                          value="buyer"
                          className="sr-only"
                        />
                        <div className={`p-4 border rounded-lg text-center transition-all duration-200 hover:shadow-md ${
                          values.user_type === 'buyer' 
                            ? 'border-brand-500 bg-brand-50' 
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}>
                          <div className="text-lg font-semibold text-gray-900 mb-1">
                            Buyer
                          </div>
                          <div className="text-sm text-gray-500">
                            Purchase products from sellers
                          </div>
                        </div>
                      </label>

                      {/* Seller Card */}
                      <label className={`relative cursor-pointer ${values.user_type === 'seller' ? 'ring-2 ring-brand-500' : ''}`}>
                        <Field
                          type="radio"
                          name="user_type"
                          value="seller"
                          className="sr-only"
                        />
                        <div className={`p-4 border rounded-lg text-center transition-all duration-200 hover:shadow-md ${
                          values.user_type === 'seller' 
                            ? 'border-brand-500 bg-brand-50' 
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}>
                          <div className="text-lg font-semibold text-gray-900 mb-1">
                            Seller
                          </div>
                          <div className="text-sm text-gray-500">
                            Sell products to customers
                          </div>
                        </div>
                      </label>
                    </div>
                    {errors.user_type && touched.user_type && (
                      <span className="text-red-400 text-sm mt-2">{errors.user_type}</span>
                    )}
                  </div>

                  {/* Given Name Field */}
                  <div className="grid">
                    <label htmlFor="given_name" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Given Name
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="given_name"
                      name="given_name"
                      placeholder="Given Name"
                      type="text"
                    />
                    {errors.given_name && touched.given_name && (
                      <span className="text-red-400 text-sm mt-2">{errors.given_name}</span>
                    )}
                  </div>

                  {/* Family Name Field */}
                  <div className="grid">
                    <label htmlFor="family_name" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Family Name
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="family_name"
                      name="family_name"
                      placeholder="Family Name"
                      type="text"
                    />
                    {errors.family_name && touched.family_name && (
                      <span className="text-red-400 text-sm mt-2">{errors.family_name}</span>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="grid">
                    <label htmlFor="email" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Email
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="email"
                      name="email"
                      placeholder="Email"
                      type="email"
                      autoComplete="email"
                    />
                    {errors.email && touched.email && (
                      <span className="text-red-400 text-sm mt-2">{errors.email}</span>
                    )}
                  </div>

                  {/* Phone Number Field */}
                  <div className="grid">
                    <label htmlFor="phone_number" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Phone Number *
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="phone_number"
                      name="phone_number"
                      placeholder="Phone Number (10 digits)"
                      type="tel"
                      autoComplete="tel"
                      maxLength={10}
                    />
                    {errors.phone_number && touched.phone_number && (
                      <span className="text-red-400 text-sm mt-2">{errors.phone_number}</span>
                    )}
                  </div>

                  {/* Phone Number 2 Field */}
                  <div className="grid">
                    <label htmlFor="phone_number2" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Phone Number 2
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="phone_number2"
                      name="phone_number2"
                      placeholder="Additional Phone Number (10 digits)"
                      type="text"
                      maxLength={10}
                    />
                    {errors.phone_number2 && touched.phone_number2 && (
                      <span className="text-red-400 text-sm mt-2">{errors.phone_number2}</span>
                    )}
                  </div>

                  {/* PAN Number Field */}
                  <div className="grid">
                    <label htmlFor="pan_number" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      PAN Number *
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="pan_number"
                      name="pan_number"
                      placeholder="PAN Number (9 digits)"
                      type="text"
                      maxLength={9}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setFieldValue('pan_number', value);
                      }}
                    />
                    {errors.pan_number && touched.pan_number && (
                      <span className="text-red-400 text-sm mt-2">{errors.pan_number}</span>
                    )}
                  </div>

                  {/* Business Registration Field */}
                  <div className="grid">
                    <label htmlFor="business_registration" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Business Registration *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="business_registration"
                        name="business_registration"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                              toast.error('File size should not exceed 5MB');
                              event.target.value = '';
                              return;
                            }
                            // Add file type validation
                            const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
                            if (!validTypes.includes(file.type)) {
                              toast.error('Invalid file type. Please upload PDF, DOC, DOCX, JPG or PNG');
                              event.target.value = '';
                              return;
                            }
                            setFieldValue("business_registration", file);
                          }
                        }}
                        className="w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-brand-50 file:text-brand-700
                          hover:file:bg-brand-100
                          cursor-pointer
                        "
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Supported formats: PDF, DOC, DOCX, JPG, PNG (max 5MB)</p>
                    {errors.business_registration && touched.business_registration && (
                      <span className="text-red-400 text-sm mt-2">{errors.business_registration}</span>
                    )}
                  </div>

                  {/* Verification Document Field */}
                  <div className="grid">
                    <label htmlFor="verification_document" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Verification Document *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="verification_document"
                        name="verification_document"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                              toast.error('File size should not exceed 5MB');
                              event.target.value = '';
                              return;
                            }
                            // Add file type validation
                            const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
                            if (!validTypes.includes(file.type)) {
                              toast.error('Invalid file type. Please upload PDF, DOC, DOCX, JPG or PNG');
                              event.target.value = '';
                              return;
                            }
                            setFieldValue("verification_document", file);
                          }
                        }}
                        className="w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-brand-50 file:text-brand-700
                          hover:file:bg-brand-100
                          cursor-pointer
                        "
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Supported formats: PDF, DOC, DOCX, JPG, PNG (max 5MB)</p>
                    {errors.verification_document && touched.verification_document && (
                      <span className="text-red-400 text-sm mt-2">{errors.verification_document}</span>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="grid sm:col-span-2">
                    <label htmlFor="password" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Password *
                    </label>
                    <Field
                      className="w-full py-2 px-3 border text-sm rounded-md bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                      id="password"
                      name="password"
                      placeholder="Password"
                      type="password"
                      autoComplete="new-password"
                    />
                    {errors.password && touched.password && (
                      <span className="text-red-400 text-sm mt-2">{errors.password}</span>
                    )}
                  </div>

                  <Button type="submit" size="lg" fullWidth disabled={isLoading} className="my-1 sm:col-span-2">
                    {!isLoading ? 'Register' : 'Registering...'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>



          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-600 hover:text-brand-700">
                Login
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RegisterPage;